import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types';
import type { RiskScoringLevel } from 'src/blocks';

import type { ScoringSettingsData } from './useRiskScoringSettingsStore';

/**
 * Maps a level to an API rating, omitting an empty description entirely.
 *
 * We must omit the key rather than emit `description: undefined`: SuperJSON
 * (used on the tRPC transport) preserves `undefined` across the wire, and the
 * data-layer then rejects it because `undefined` isn't a valid jsonb value.
 * The GraphQL path drops undefined keys during JSON serialization, which is
 * why empty descriptions only broke on tRPC.
 */
const toRating = (level: RiskScoringLevel) => ({
  title: level.title,
  value: level.value,
  color: level.color,
  ...(level.description ? { description: level.description } : {}),
});

/**
 * Transforms Zustand store state to API config format for the risk assessment result configuration.
 * Returns a config object compatible with the jsonb Config field in the database.
 */
export const transformToApiConfig = (
  state: Omit<ScoringSettingsData, 'id' | 'originalTimestamp'>
): RiskAssessmentResultConfig => {
  return {
    likelihood: {
      ratings: state.likelihoodLevels.map(toRating),
    },
    impact: {
      ratings: state.impactLevels.map(toRating),
      categories: state.impactCategories.map((cat) => ({
        name: cat.name,
        color: cat.color,
      })),
      aggregation: state.impactAggregation,
    },
    matrix: state.matrix.map((entry) => ({
      title: entry.title,
      value: entry.value,
      color: entry.color,
      likelihood: entry.likelihood,
      impact: entry.impact,
    })),
  };
};
